import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ProtectedButton } from "@/components/protected-button";
import { usePermission } from "@/hooks/use-permission";
import { usePermissions } from "@/providers/permissions-provider";
import { Shield, Eye, Edit, Trash2, Plus, Lock, CheckCircle2, XCircle, Lightbulb } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export function PermissionsDemo() {
  const { currentUser, currentProfile, hasPermission } = usePermissions();
  
  // Check various permissions
  const canViewInventory = usePermission("inventory.stock-items.view");
  const canCreateInventory = usePermission("inventory.stock-items.create");
  const canEditInventory = usePermission("inventory.stock-items.edit");
  const canDeleteInventory = usePermission("inventory.stock-items.delete");
  
  const canViewPurchasing = usePermission("purchasing.orders.view");
  const canManageUsers = usePermission("settings.users.manage");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Shield className="h-8 w-8 text-primary" />
          Permissions System Demo
        </h1>
        <p className="text-muted-foreground mt-2">
          This page demonstrates how the permissions system works in the application.
        </p>
      </div>

      {/* Current User Info */}
      <Card>
        <CardHeader>
          <CardTitle>Current User & Profile</CardTitle>
          <CardDescription>Your active user session and assigned permissions</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">User Name</p>
              <p className="text-lg font-semibold">{currentUser?.name || "Not logged in"}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Email</p>
              <p className="text-lg">{currentUser?.email || "N/A"}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">User Profile</p>
              <p className="text-lg font-semibold">{currentProfile?.name || "No profile"}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Status</p>
              <Badge variant={currentUser?.status === "active" ? "default" : "secondary"}>
                {currentUser?.status || "Unknown"}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Permission Checks */}
      <Card>
        <CardHeader>
          <CardTitle>Permission Checks</CardTitle>
          <CardDescription>
            Examples of checking permissions using the usePermission hook
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PermissionCheck
              label="View Inventory"
              permission="inventory.stock-items.view"
              hasPermission={canViewInventory}
            />
            <PermissionCheck
              label="Create Inventory"
              permission="inventory.stock-items.create"
              hasPermission={canCreateInventory}
            />
            <PermissionCheck
              label="Edit Inventory"
              permission="inventory.stock-items.edit"
              hasPermission={canEditInventory}
            />
            <PermissionCheck
              label="Delete Inventory"
              permission="inventory.stock-items.delete"
              hasPermission={canDeleteInventory}
            />
            <PermissionCheck
              label="View Purchasing"
              permission="purchasing.orders.view"
              hasPermission={canViewPurchasing}
            />
            <PermissionCheck
              label="Manage Users"
              permission="settings.users.manage"
              hasPermission={canManageUsers}
            />
          </div>
        </CardContent>
      </Card>

      {/* Conditional Rendering */}
      <Card>
        <CardHeader>
          <CardTitle>Conditional Rendering</CardTitle>
          <CardDescription>
            Components that show/hide based on permissions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {canViewInventory ? (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Inventory Access Granted</AlertTitle>
              <AlertDescription>
                You have permission to view inventory items. This section would show inventory data.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert variant="destructive">
              <Lock className="h-4 w-4" />
              <AlertTitle>Inventory Access Denied</AlertTitle>
              <AlertDescription>
                You don't have permission to view inventory items. Contact your administrator.
              </AlertDescription>
            </Alert>
          )}

          {canManageUsers && (
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertTitle>Admin Access</AlertTitle>
              <AlertDescription>
                You have administrative permissions to manage users. This section is only visible to admins.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Protected Buttons */}
      <Card>
        <CardHeader>
          <CardTitle>Protected Buttons</CardTitle>
          <CardDescription>
            Buttons that automatically hide when user lacks permission
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <ProtectedButton
              permission="inventory.stock-items.view"
              variant="outline"
            >
              <Eye className="h-4 w-4 mr-2" />
              View Items
            </ProtectedButton>

            <ProtectedButton
              permission="inventory.stock-items.create"
              variant="default"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Item
            </ProtectedButton>

            <ProtectedButton
              permission="inventory.stock-items.edit"
              variant="secondary"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Item
            </ProtectedButton>

            <ProtectedButton
              permission="inventory.stock-items.delete"
              variant="destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Item
            </ProtectedButton>

            <ProtectedButton
              permission="settings.users.manage"
              variant="outline"
            >
              <Shield className="h-4 w-4 mr-2" />
              Manage Users (Admin Only)
            </ProtectedButton>
          </div>

          <p className="text-sm text-muted-foreground mt-4 flex items-start gap-2">
            <Lightbulb className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Tip:</strong> Buttons you don't have permission for are automatically hidden.
              Try switching users with the User Switcher in the top-right corner!
            </span>
          </p>
        </CardContent>
      </Card>

      {/* Manual Button Control */}
      <Card>
        <CardHeader>
          <CardTitle>Manual Permission Control</CardTitle>
          <CardDescription>
            Regular buttons with manual permission checks
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={!canViewInventory}
            >
              <Eye className="h-4 w-4 mr-2" />
              View (Disabled if no permission)
            </Button>

            {canCreateInventory && (
              <Button variant="default">
                <Plus className="h-4 w-4 mr-2" />
                Create (Hidden if no permission)
              </Button>
            )}

            <Button
              variant="secondary"
              disabled={!canEditInventory}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit (Disabled if no permission)
            </Button>

            {canDeleteInventory ? (
              <Button variant="destructive">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete (Visible)
              </Button>
            ) : (
              <Button variant="outline" disabled>
                <Lock className="h-4 w-4 mr-2" />
                Delete (No Permission)
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Code Examples */}
      <Card>
        <CardHeader>
          <CardTitle>Code Examples</CardTitle>
          <CardDescription>How to implement permissions in your components</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">1. Using the usePermission hook:</h4>
            <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
{`import { usePermission } from "@/hooks/use-permission";

function MyComponent() {
  const canEdit = usePermission("inventory.stock-items.edit");
  
  return (
    <div>
      {canEdit && <Button>Edit Item</Button>}
    </div>
  );
}`}
            </pre>
          </div>

          <div>
            <h4 className="font-semibold mb-2">2. Using ProtectedButton:</h4>
            <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
{`import { ProtectedButton } from "@/components/protected-button";

function MyComponent() {
  return (
    <ProtectedButton 
      permission="inventory.stock-items.delete"
      variant="destructive"
      onClick={handleDelete}
    >
      Delete Item
    </ProtectedButton>
  );
}`}
            </pre>
          </div>

          <div>
            <h4 className="font-semibold mb-2">3. Using the permissions context:</h4>
            <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
{`import { usePermissions } from "@/providers/permissions-provider";

function MyComponent() {
  const { hasPermission, currentUser } = usePermissions();
  
  if (!hasPermission("inventory.stock-items.view")) {
    return <div>Access Denied</div>;
  }
  
  return <div>Welcome {currentUser?.name}</div>;
}`}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Helper component to display permission check results
function PermissionCheck({ 
  label, 
  permission, 
  hasPermission 
}: { 
  label: string; 
  permission: string; 
  hasPermission: boolean;
}) {
  return (
    <div className="flex items-center justify-between p-3 border rounded-lg">
      <div className="flex items-center gap-2">
        {hasPermission ? (
          <CheckCircle2 className="h-5 w-5 text-green-600" />
        ) : (
          <XCircle className="h-5 w-5 text-red-600" />
        )}
        <div>
          <p className="font-medium">{label}</p>
          <p className="text-xs text-muted-foreground">{permission}</p>
        </div>
      </div>
      <Badge variant={hasPermission ? "default" : "secondary"}>
        {hasPermission ? "Granted" : "Denied"}
      </Badge>
    </div>
  );
}
